import { useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import {
  ChevronDown,
  ChevronRight,
  MoreHorizontal,
  Play,
  CheckCircle2,
  Copy,
  Trash2,
  CalendarDays,
  GripVertical,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { OverviewSprint, SprintIssueItem } from '@hygge/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useUpdateSprint, useReorderSprintIssues } from '@/hooks/use-sprints';
import { useDuplicateSprint } from '@/hooks/use-board-overview';
import { extractErrorMessage } from '@/lib/errors';
import { isSprintExpanded, setSprintExpanded } from '../../lib/sprint-collapse';
import { SprintStatusBadge } from '../sprint-status-badge';
import { AssigneeStack } from '../assignee-stack';
import { SprintIssueAdd } from './sprint-issue-add';

interface SprintBlockProps {
  sprint: OverviewSprint;
  boardId: string;
  canManage: boolean;
  canDelete: boolean;
  isAnotherSprintActive: boolean;
  onStart: (id: string) => void;
  onComplete: (id: string) => void;
  onDelete: (id: string) => void;
  onOpenTask: (key: string) => void;
}

export function SprintBlock({
  sprint,
  boardId,
  canManage,
  canDelete,
  isAnotherSprintActive,
  onStart,
  onComplete,
  onDelete,
  onOpenTask,
}: SprintBlockProps) {
  const updateSprint = useUpdateSprint();
  const duplicateSprint = useDuplicateSprint(boardId);
  const reorderIssues = useReorderSprintIssues(boardId);

  // Sprint-level sortable (this block is a row inside the section's SortableContext).
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sprint.id, disabled: !canManage });
  const sortableStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  /**
   * Collapsed unless the reader has opened this sprint before.
   *
   * The old default collapsed only `completed` and `cancelled` sprints — and
   * every sprint in this product is `active`, so it never fired once: the
   * Overview opened with a fifty-four-row issue list expanded, three screens
   * tall, before anyone had asked to see it. The choice now persists per sprint
   * (see `sprint-collapse.ts`), because it used to live in bare `useState` and
   * a remount threw it away every time.
   */
  const [expanded, setExpanded] = useState(() => isSprintExpanded(sprint.id));
  const collapsed = !expanded;

  const toggleCollapsed = () => {
    const next = !expanded;
    setExpanded(next);
    setSprintExpanded(sprint.id, next);
  };

  // Issue-level DnD (a per-block context so a drag never crosses sprints).
  const issueSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  // Optimistic order held only during/after a drag to avoid snap-back.
  const [issueOrder, setIssueOrder] = useState<string[] | null>(null);
  const issueIds = issueOrder ?? sprint.issues.map((i) => i.id);
  const orderedIssues = issueIds
    .map((id) => sprint.issues.find((i) => i.id === id))
    .filter((i): i is SprintIssueItem => i != null);

  const handleIssueDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const current = sprint.issues.map((i) => i.id);
    const oldIdx = current.indexOf(active.id as string);
    const newIdx = current.indexOf(over.id as string);
    if (oldIdx === -1 || newIdx === -1) return;

    const next = arrayMove(current, oldIdx, newIdx);
    setIssueOrder(next);
    reorderIssues.mutate(
      { sprintId: sprint.id, taskIds: next },
      {
        onError: (e) => toast.error(extractErrorMessage(e, 'Failed to reorder issues')),
        onSettled: () => setIssueOrder(null),
      },
    );
  };

  // Inline name editing
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(sprint.name);

  // Dates popover
  const [datesOpen, setDatesOpen] = useState(false);
  const [startDraft, setStartDraft] = useState('');
  const [endDraft, setEndDraft] = useState('');

  // Description — re-sync from the server whenever the field isn't being edited,
  // so a concurrent update elsewhere isn't silently reverted by a stale draft.
  const [descDraft, setDescDraft] = useState(sprint.description ?? '');
  const descFocused = useRef(false);
  useEffect(() => {
    if (!descFocused.current) setDescDraft(sprint.description ?? '');
  }, [sprint.description]);

  const startName = () => {
    if (!canManage) return;
    setNameDraft(sprint.name);
    setEditingName(true);
  };

  const saveName = () => {
    setEditingName(false);
    const next = nameDraft.trim();
    if (next && next !== sprint.name) {
      updateSprint.mutate(
        { id: sprint.id, name: next },
        { onError: (e) => toast.error(extractErrorMessage(e, 'Failed to update sprint')) },
      );
    }
  };

  const openDates = (open: boolean) => {
    if (open) {
      setStartDraft(sprint.startDate ? sprint.startDate.slice(0, 10) : '');
      setEndDraft(sprint.endDate ? sprint.endDate.slice(0, 10) : '');
    }
    setDatesOpen(open);
  };

  const saveDates = () => {
    updateSprint.mutate(
      { id: sprint.id, startDate: startDraft || null, endDate: endDraft || null },
      {
        onSuccess: () => setDatesOpen(false),
        onError: (e) => toast.error(extractErrorMessage(e, 'Failed to update sprint')),
      },
    );
  };

  const saveDescription = () => {
    // Backend clears the description with an empty string (the field is non-nullable).
    const next = descDraft.trim();
    if (next !== (sprint.description ?? '')) {
      updateSprint.mutate(
        { id: sprint.id, description: next },
        { onError: (e) => toast.error(extractErrorMessage(e, 'Failed to update sprint')) },
      );
    }
  };

  const duplicate = () => {
    duplicateSprint.mutate(sprint.id, {
      onSuccess: () => toast.success('Sprint duplicated'),
      onError: (e) => toast.error(extractErrorMessage(e, 'Failed to duplicate sprint')),
    });
  };

  const dateLabel =
    sprint.startDate || sprint.endDate
      ? `${sprint.startDate ? format(new Date(sprint.startDate), 'MMM d') : '—'} – ${
          sprint.endDate ? format(new Date(sprint.endDate), 'MMM d') : '—'
        }`
      : 'Set dates';

  // When canManage, this button is the PopoverTrigger — Radix drives open state via
  // onOpenChange={openDates}, so it must not also toggle the popover itself.
  const datesButton = (
    <button
      type="button"
      className={cn(
        'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-muted-foreground',
        canManage && 'hover:bg-accent hover:text-foreground cursor-pointer',
      )}
      disabled={!canManage}
    >
      <CalendarDays className="h-3 w-3" />
      {dateLabel}
    </button>
  );

  return (
    <div
      ref={setNodeRef}
      style={sortableStyle}
      className={cn(
        'rounded-lg border border-border bg-card',
        isDragging && 'relative z-10 opacity-70 shadow-lg',
      )}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2.5">
        {canManage && (
          <button
            type="button"
            className="shrink-0 cursor-grab touch-none text-muted-foreground/60 hover:text-foreground transition-colors active:cursor-grabbing"
            aria-label="Reorder sprint"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          onClick={toggleCollapsed}
          className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer shrink-0"
          aria-label={collapsed ? 'Expand sprint' : 'Collapse sprint'}
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </button>

        {editingName ? (
          <Input
            autoFocus
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onBlur={saveName}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.currentTarget.blur();
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                setEditingName(false);
              }
            }}
            className="h-7 max-w-[16rem] text-sm font-semibold"
          />
        ) : canManage ? (
          <button
            type="button"
            onClick={startName}
            className="truncate text-sm font-semibold text-foreground hover:text-hygge transition-colors cursor-pointer"
          >
            {sprint.name}
          </button>
        ) : (
          <span className="truncate text-sm font-semibold text-foreground">{sprint.name}</span>
        )}

        <SprintStatusBadge status={sprint.status} className="shrink-0" />

        {/* Dates control */}
        {canManage ? (
          <Popover open={datesOpen} onOpenChange={openDates}>
            <PopoverTrigger asChild>{datesButton}</PopoverTrigger>
            <PopoverContent align="start" className="w-64 space-y-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Start date</label>
                <Input
                  type="date"
                  value={startDraft}
                  onChange={(e) => setStartDraft(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">End date</label>
                <Input
                  type="date"
                  value={endDraft}
                  onChange={(e) => setEndDraft(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setDatesOpen(false)}
                  className="cursor-pointer"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={saveDates}
                  disabled={updateSprint.isPending}
                  className="cursor-pointer"
                >
                  Save
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        ) : (
          datesButton
        )}

        <span className="ml-auto text-xs text-muted-foreground tabular-nums shrink-0">
          Issues ({sprint.issues.length})
        </span>

        {/* Context menu */}
        {canManage && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer shrink-0"
                aria-label="Sprint actions"
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {sprint.status === 'planning' && (
                <>
                  {isAnotherSprintActive ? (
                    <DropdownMenuItem
                      disabled
                      className="flex-col items-start gap-0.5"
                      onSelect={(e) => e.preventDefault()}
                    >
                      <span className="flex items-center">
                        <Play className="h-3.5 w-3.5 mr-2" />
                        Start sprint
                      </span>
                      <span className="pl-[22px] text-[10px] text-muted-foreground">
                        Another sprint is already active
                      </span>
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem
                      onClick={() => onStart(sprint.id)}
                      className="cursor-pointer"
                    >
                      <Play className="h-3.5 w-3.5 mr-2" />
                      Start sprint
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => openDates(true)} className="cursor-pointer">
                    <CalendarDays className="h-3.5 w-3.5 mr-2" />
                    Edit dates
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={duplicate}
                    disabled={duplicateSprint.isPending}
                    className="cursor-pointer"
                  >
                    <Copy className="h-3.5 w-3.5 mr-2" />
                    Duplicate sprint
                  </DropdownMenuItem>
                  {canDelete && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => onDelete(sprint.id)}
                        className="cursor-pointer text-destructive focus:text-destructive"
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-2" />
                        Delete sprint
                      </DropdownMenuItem>
                    </>
                  )}
                </>
              )}

              {sprint.status === 'active' && (
                <>
                  <DropdownMenuItem
                    onClick={() => onComplete(sprint.id)}
                    className="cursor-pointer"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5 mr-2" />
                    Complete sprint
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => openDates(true)} className="cursor-pointer">
                    <CalendarDays className="h-3.5 w-3.5 mr-2" />
                    Edit dates
                  </DropdownMenuItem>
                </>
              )}

              {(sprint.status === 'completed' || sprint.status === 'cancelled') && (
                <>
                  <DropdownMenuItem
                    onClick={duplicate}
                    disabled={duplicateSprint.isPending}
                    className="cursor-pointer"
                  >
                    <Copy className="h-3.5 w-3.5 mr-2" />
                    Duplicate sprint
                  </DropdownMenuItem>
                  {canDelete && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => onDelete(sprint.id)}
                        className="cursor-pointer text-destructive focus:text-destructive"
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-2" />
                        Delete sprint
                      </DropdownMenuItem>
                    </>
                  )}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {!collapsed && (
        <div className="space-y-3 border-t border-border/60 px-3 py-3">
          {/* Description */}
          {canManage ? (
            <Textarea
              value={descDraft}
              onChange={(e) => setDescDraft(e.target.value)}
              onFocus={() => {
                descFocused.current = true;
              }}
              onBlur={() => {
                descFocused.current = false;
                saveDescription();
              }}
              placeholder="Add a description…"
              rows={2}
              className="resize-none text-sm"
            />
          ) : sprint.description ? (
            <p className="whitespace-pre-wrap text-sm text-muted-foreground">
              {sprint.description}
            </p>
          ) : null}

          {/* Issues list — reorderable within this sprint only (its own DnD context) */}
          {sprint.issues.length === 0 ? (
            <p className="px-2 py-1.5 text-sm text-muted-foreground">No issues yet</p>
          ) : (
            <DndContext
              sensors={issueSensors}
              collisionDetection={closestCenter}
              onDragEnd={handleIssueDragEnd}
            >
              <SortableContext items={issueIds} strategy={verticalListSortingStrategy}>
                <div className="space-y-0.5">
                  {orderedIssues.map((issue) => (
                    <SortableIssueRow
                      key={issue.id}
                      issue={issue}
                      canReorder={canManage}
                      onOpenTask={onOpenTask}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}

          {/* Inline add-or-create issue */}
          {canManage && (
            <SprintIssueAdd
              boardId={boardId}
              sprintId={sprint.id}
              existingIssueIds={sprint.issues.map((i) => i.id)}
            />
          )}
        </div>
      )}
    </div>
  );
}

interface SortableIssueRowProps {
  issue: SprintIssueItem;
  canReorder: boolean;
  onOpenTask: (key: string) => void;
}

/**
 * One sprint issue row: a grip handle (drag/keyboard reorder within the sprint)
 * plus the clickable summary that opens the task. The grip carries the sortable
 * listeners so clicking the row still opens the task rather than starting a drag.
 */
function SortableIssueRow({ issue, canReorder, onOpenTask }: SortableIssueRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: issue.id, disabled: !canReorder });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'flex w-full items-center gap-1 rounded-md pr-2 transition-colors hover:bg-accent',
        isDragging && 'relative z-10 bg-accent opacity-80',
      )}
    >
      {canReorder && (
        <button
          type="button"
          className="shrink-0 cursor-grab touch-none px-0.5 py-1.5 text-muted-foreground/50 hover:text-foreground active:cursor-grabbing"
          aria-label="Reorder issue"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
      )}
      <button
        type="button"
        onClick={() => onOpenTask(issue.key)}
        className={cn(
          'flex min-w-0 flex-1 items-center gap-2.5 rounded-md py-1.5 text-left cursor-pointer',
          !canReorder && 'px-2',
        )}
      >
        <span
          className="h-2 w-2 shrink-0 rounded-full"
          style={{ backgroundColor: issue.status.color }}
        />
        <span className="shrink-0 font-mono text-xs text-muted-foreground">{issue.key}</span>
        <span className="flex-1 truncate text-sm">{issue.title}</span>
        {issue.storyPoints != null && (
          <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] tabular-nums text-muted-foreground">
            {issue.storyPoints}
          </span>
        )}
        {issue.assignees.length > 0 && (
          <AssigneeStack people={issue.assignees} size="sm" className="shrink-0" />
        )}
      </button>
    </div>
  );
}
